import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { gql, useMutation } from '@apollo/client';
import { FileUp, Upload, X } from "lucide-react";
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

const GENERATE_OR = gql`
   mutation GenerateOR($Id: ID!, $input: GenerateORInput!) {
      generateOR(Id: $Id, input: $input) {
         _id
         OOPstatus
         officialReceipt {
            orNumber
            dateIssued
            amount
            paymentMethod
            remarks
            receiptImage
         }
         applicationId
      }
   }
`;

const GenerateORModal = ({ oop, isOpen, onClose, onComplete }) => {
   const [orNumber, setOrNumber] = useState('');
   const [remarks, setRemarks] = useState('');
   const [receiptImage, setReceiptImage] = useState(null);
   const [isGenerating, setIsGenerating] = useState(false);
   const [isDragging, setIsDragging] = useState(false);
   const fileInputRef = useRef(null);

   const [generateOR] = useMutation(GENERATE_OR);

   const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (file) {
         if (file.size > 5 * 1024 * 1024) { // 5MB limit
            toast.error('File size should be less than 5MB');
            return;
         }
         setReceiptImage(file);
      }
   };

   const convertFileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.readAsDataURL(file);
         reader.onload = () => resolve(reader.result);
         reader.onerror = (error) => reject(error);
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (!orNumber.trim()) {
         toast.error('OR number is required');
         return;
      }

      if (!receiptImage) {
         toast.error('Receipt image is required');
         return;
      }

      setIsGenerating(true);
      try {
         const base64Image = await convertFileToBase64(receiptImage);
         const { data } = await generateOR({
            variables: {
               Id: oop._id,
               input: {
                  orNumber,
                  remarks,
                  amount: parseFloat(oop.totalAmount),
                  paymentMethod: oop.isManualPayment ? 'MANUAL' : 'GCASH',
                  receiptImage: base64Image
               }
            }
         });

         if (data.generateOR) {
            toast.success('Official Receipt generated successfully');
            onComplete();
            onClose();
         }
      } catch (error) {
         toast.error(`Failed to generate Official Receipt: ${error.message}`);
      } finally {
         setIsGenerating(false);
      }
   };

   const handleRemoveFile = () => {
      setReceiptImage(null);
      // Reset the file input
      if (fileInputRef.current) {
         fileInputRef.current.value = '';
      }
   };

   const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
   };

   const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
   };

   const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
         if (file.type.startsWith('image/')) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
               toast.error('File size should be less than 5MB');
               return;
            }
            setReceiptImage(file);
            if (fileInputRef.current) {
               // Create a new FileList-like object
               const dataTransfer = new DataTransfer();
               dataTransfer.items.add(file);
               fileInputRef.current.files = dataTransfer.files;
            }
         } else {
            toast.error('Please upload an image file');
         }
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
               <DialogTitle>Generate Official Receipt</DialogTitle>
               <DialogDescription>
                  Enter OR details and upload the scanned receipt.
               </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
               <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                     <Label>Payment Details</Label>
                     <div className="text-sm">
                        <p><span className="font-medium">Application No:</span> {oop.applicationId}</p>
                        <p><span className="font-medium">Bill No:</span> {oop.billNo}</p>
                        <p><span className="font-medium">Amount:</span> ₱{oop.totalAmount?.toFixed(2)}</p>
                     </div>
                  </div>

                  <div className="grid gap-2">
                     <Label htmlFor="orNumber">OR Number</Label>
                     <Input
                        id="orNumber"
                        value={orNumber}
                        onChange={(e) => setOrNumber(e.target.value)}
                        placeholder="Enter OR number"
                        required
                     />
                  </div>

                  <div className="grid gap-2">
                     <Label htmlFor="receipt">Upload Receipt</Label>
                     <div className="flex flex-col items-center justify-center gap-4">
                        {!receiptImage ? (
                           <div
                              onClick={() => fileInputRef.current?.click()}
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={handleDrop}
                              className={`w-full border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors duration-200 flex flex-col items-center justify-center gap-2
                                 ${isDragging
                                    ? 'border-green-500 bg-green-50 dark:border-green-400 dark:bg-green-950/10'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-green-500 dark:hover:border-green-400'
                                 }`}
                           >
                              <Upload className={`h-8 w-8 ${isDragging ? 'text-green-500 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} />
                              <div className="text-sm text-center">
                                 <p className={`font-medium ${isDragging ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'}`}>
                                    {isDragging ? 'Drop your file here' : 'Click to upload'}
                                 </p>
                                 <p className={`${isDragging ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                    or drag and drop
                                 </p>
                                 <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                    Maximum file size: 5MB
                                 </p>
                              </div>
                           </div>
                        ) : (
                           <div className="w-full bg-muted/50 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                 <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                                    <FileUp className="h-4 w-4" />
                                    <span className="truncate max-w-[200px]">{receiptImage.name}</span>
                                 </div>
                                 <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                                    onClick={handleRemoveFile}
                                 >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">Remove file</span>
                                 </Button>
                              </div>
                           </div>
                        )}
                        <Input
                           id="receipt"
                           ref={fileInputRef}
                           type="file"
                           accept="image/*"
                           onChange={handleFileChange}
                           className="hidden"
                           required
                        />
                     </div>
                  </div>

                  <div className="grid gap-2">
                     <Label htmlFor="remarks">Remarks</Label>
                     <Textarea
                        id="remarks"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Add any remarks..."
                     />
                  </div>
               </div>

               <div className="flex justify-end gap-2">
                  <Button
                     type="button"
                     variant="outline"
                     onClick={onClose}
                  >
                     Cancel
                  </Button>
                  <Button
                     type="submit"
                     className="bg-green-600 hover:bg-green-700"
                     disabled={isGenerating}
                  >
                     {isGenerating ? 'Generating...' : 'Generate OR'}
                  </Button>
               </div>
            </form>
         </DialogContent>
      </Dialog>
   );
};

export default GenerateORModal;
